import React from 'react';
import { BookText, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { PolicyFitStatus } from '@ballerine/common';

import { markdownComponents } from '../../report-shared/components/markdown-components';
import { PolicyFitIcon } from '../../report-shared/components/PolicyFitBadge/PolicyFitBadge';
import { ReportWrapper } from '../../report-shared/components/ReportWrapper';

type YourPolicyFitSectionProps = {
  policyFit?: {
    status?: PolicyFitStatus;
    description?: string | null;
    displayName?: string | null;
    version?: number | null;
    originalPolicyFileId?: string | null;
  } | null;
  customer?: {
    displayName?: string | null;
  } | null;
  onOpenOriginalPolicy?: () => void;
};

export const YourPolicyFitSection: React.FC<YourPolicyFitSectionProps> = ({
  policyFit,
  customer,
  onOpenOriginalPolicy,
}) => {
  if (!policyFit) {
    return null;
  }

  const showOriginalPolicyLink = policyFit.originalPolicyFileId && onOpenOriginalPolicy;

  return (
    <ReportWrapper showTooltip={false}>
      <div className="flex flex-col gap-4 pt-10">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold text-slate-800">Your Policy Fit</h2>
          <div className="flex items-center gap-4">
            {customer?.displayName && policyFit.version != null && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <BookText className="h-4 w-4" />
                Policy {customer?.displayName} v.{policyFit.version}
                {showOriginalPolicyLink && (
                  <button
                    type="button"
                    onClick={onOpenOriginalPolicy}
                    className="ml-1 inline-flex items-center text-purple-600 hover:text-purple-700"
                    title="View original policy document"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            )}
            <PolicyFitIcon policyFit={policyFit} variant="simple" withStatusText={true} />
          </div>
        </div>

        {policyFit.description && (
          <div className="prose prose-sm max-w-none text-sm leading-snug">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {policyFit.description}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </ReportWrapper>
  );
};
